import { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { 
  Star, Send, Video, CheckCircle, Loader2, User, Mail, Phone, Briefcase
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ReviewSubmissionForm = ({ onSuccess, className = "" }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    rating: 5,
    review: '',
    video_url: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [hoveredRating, setHoveredRating] = useState(0);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRatingClick = (rating) => {
    setFormData(prev => ({ ...prev, rating }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!formData.review.trim()) {
      toast.error('Please enter your review');
      return;
    }
    if (formData.review.length < 20) {
      toast.error('Please write at least 20 characters in your review');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews/submit`, {
        name: formData.name,
        email: formData.email || null,
        phone: formData.phone || null,
        position: formData.position || null,
        rating: formData.rating,
        review: formData.review,
        video_url: formData.video_url || null
      });
      
      setSubmitted(true);
      toast.success('Thank you! Your review has been submitted.');
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error('Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Card className={`${className}`} data-testid="review-submission-success">
        <CardContent className="p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-2">Thank You!</h3>
          <p className="text-slate-600 mb-4">
            Your review has been submitted successfully. It will be visible after approval.
          </p>
          <Button 
            variant="outline" 
            onClick={() => {
              setSubmitted(false);
              setFormData({
                name: '',
                email: '',
                phone: '',
                position: '',
                rating: 5,
                review: '',
                video_url: ''
              });
            }}
          >
            Submit Another Review
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`${className}`} data-testid="review-submission-form">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Star className="w-5 h-5 text-yellow-500" />
          Share Your Experience
        </CardTitle>
        <CardDescription>
          We'd love to hear about your experience with our services
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Rating Selection */}
          <div className="space-y-2">
            <Label>Your Rating *</Label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => handleRatingClick(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="focus:outline-none transition-transform hover:scale-110"
                  data-testid={`rating-star-${star}`}
                >
                  <Star 
                    className={`w-8 h-8 transition-colors ${
                      star <= (hoveredRating || formData.rating)
                        ? 'text-yellow-400 fill-yellow-400'
                        : 'text-slate-300'
                    }`}
                  />
                </button>
              ))}
              <span className="ml-2 text-slate-600 self-center">
                {formData.rating} out of 5
              </span>
            </div>
          </div>

          {/* Name & Position */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="flex items-center gap-1">
                <User className="w-4 h-4" /> Your Name *
              </Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="John Doe"
                required
                data-testid="review-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="position" className="flex items-center gap-1">
                <Briefcase className="w-4 h-4" /> Position/Company
              </Label>
              <Input
                id="position"
                name="position"
                value={formData.position}
                onChange={handleChange}
                placeholder="Manager, ABC Company"
                data-testid="review-position-input"
              />
            </div>
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-1">
                <Mail className="w-4 h-4" /> Email (Optional)
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="john@example.com"
                data-testid="review-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone" className="flex items-center gap-1">
                <Phone className="w-4 h-4" /> Phone (Optional)
              </Label>
              <Input
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+91 98765 43210"
                data-testid="review-phone-input"
              />
            </div>
          </div>

          {/* Review Text */}
          <div className="space-y-2">
            <Label htmlFor="review">Your Review *</Label>
            <Textarea
              id="review"
              name="review"
              value={formData.review}
              onChange={handleChange}
              placeholder="Share your experience with our products and services..."
              rows={4}
              required
              minLength={20}
              data-testid="review-text-input"
            />
            <p className="text-xs text-slate-500">
              {formData.review.length}/20 characters minimum
            </p>
          </div>

          {/* Video URL */}
          <div className="space-y-2">
            <Label htmlFor="video_url" className="flex items-center gap-1">
              <Video className="w-4 h-4" /> Video Testimonial URL (Optional)
            </Label>
            <Input
              id="video_url"
              name="video_url"
              value={formData.video_url}
              onChange={handleChange}
              placeholder="https://youtube.com/watch?v=... or Instagram reel URL"
              data-testid="review-video-input"
            />
            <p className="text-xs text-slate-500">
              Share a YouTube video or Instagram reel of your testimonial
            </p>
          </div>

          {/* Submit Button */}
          <Button 
            type="submit" 
            className="w-full bg-sky-600 hover:bg-sky-700"
            disabled={submitting}
            data-testid="submit-review-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Submit Your Review
              </>
            )}
          </Button>

          <p className="text-xs text-slate-500 text-center">
            Your review will be published after verification. We may contact you for confirmation.
          </p>
        </form>
      </CardContent>
    </Card>
  );
};

export default ReviewSubmissionForm;
